import { useEffect, useMemo, useRef, useState } from 'react'
import _ from 'lodash'
import { IDoesFilterPassParams } from 'ag-grid-community'
import { CustomFilterProps, useGridFilter } from 'ag-grid-react'
import { APIResponse } from '../../../../../../lib/commons/api'
import { BulkSheetFilter, FilterFooter, FilterSelect } from '../common'
import DateInput from '../../../../../components/editors/input/DateInput'
import { Box } from '@mui/material'
import {
  DateVO,
  dateVoService,
} from '../../../../../../domain/value-object/DateVO'
import { useSelector } from 'react-redux'
import { AllState } from '../../../../../../store'
import usePrevious from '../../../../../hooks/usePrevious'
import { intl } from '../../../../../../i18n'

export type DateFilter = {
  operator: DateFilterOperator
  value: string
}

export enum DateFilterOperator {
  EQUALS = 'EQUALS',
  BLANK = 'BLANK',
  AFTER = 'AFTER',
  BEFORE = 'BEFORE',
}

type Props = CustomFilterProps & {
  fetch: (v: DateFilter) => Promise<APIResponse>
}

const buildDateFilterModel = (
  operator: DateFilterOperator,
  value: DateVO | undefined
): DateFilter | null => {
  const isActive = operator === DateFilterOperator.BLANK || !!value
  return isActive
    ? { operator, value: value ? dateVoService.format(value) : '' }
    : null
}

const getLabel = (operator: DateFilterOperator): string => {
  switch (operator) {
    case DateFilterOperator.EQUALS:
      return intl.formatMessage({
        id: 'bulksheet.filter.dateFilter.operator.equal',
      })
    case DateFilterOperator.BLANK:
      return intl.formatMessage({
        id: 'bulksheet.filter.dateFilter.operator.notSet',
      })
    case DateFilterOperator.AFTER:
      return intl.formatMessage({
        id: 'bulksheet.filter.dateFilter.operator.greaterThanEqual',
      })
    case DateFilterOperator.BEFORE:
      return intl.formatMessage({
        id: 'bulksheet.filter.dateFilter.operator.lessThanEqual',
      })
    default:
      return ''
  }
}

const getDateModelAsString = (
  operator: DateFilterOperator,
  value: DateVO | undefined
): string => {
  if (operator === DateFilterOperator.BLANK) {
    return `(${operator.charAt(0)}${_.lowerCase(operator.slice(1))})`
  }
  return `${
    operator === DateFilterOperator.EQUALS
      ? '='
      : operator === DateFilterOperator.AFTER
        ? '≥'
        : operator === DateFilterOperator.BEFORE
          ? '≤'
          : ''
  } ${value && dateVoService.format(value, 'YYYY/MM/DD')}`
}

export const ServerSideDateFilter = ({
  model,
  onModelChange,
  api,
  fetch: fetchFn,
}: Props) => {
  const hasRequiredSaveData = useSelector<AllState>(
    state => state.hasRequiredSaveData.hasRequiredSaveData
  )
  const prevHasRequiredSaveData = usePrevious(hasRequiredSaveData)

  // Derive state from model (single source of truth)
  const operator =
    (model?.operator as DateFilterOperator) ?? DateFilterOperator.EQUALS
  const rawValue = model?.value as string | undefined
  const dateValue = rawValue ? dateVoService.construct(rawValue) : undefined

  // UI-local state for async filtering
  const [loading, isLoading] = useState(false)
  const [filteredIds, setFilteredIds] = useState<Set<string> | undefined>(
    undefined
  )

  const addedRowIds = useRef<string[]>()
  useEffect(() => {
    if (addedRowIds.current) return
    addedRowIds.current = []
  }, [])

  const fetchFilteredIds = async (v: DateFilter) => {
    const response = await fetchFn(v)
    setFilteredIds(new Set(response.json))
  }

  const search = useMemo(
    () =>
      _.debounce(async (v: DateFilter | null) => {
        if (!v) {
          setFilteredIds(undefined)
          return
        }
        try {
          isLoading(true)
          await fetchFilteredIds(v)
        } finally {
          isLoading(false)
        }
      }, 300),
    []
  )

  // When criteria changes (model changes), trigger debounced search
  const modelKey = JSON.stringify(model)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- modelKey は model の値比較用。model 自体は毎レンダーで新規オブジェクトになるため deps に入れると毎回発火する。search は useMemo で安定
  useEffect(() => {
    search(model as DateFilter | null)
  }, [modelKey])

  // When filteredIds changes, trigger re-filtering
  // eslint-disable-next-line react-hooks/exhaustive-deps -- api は AG Grid から渡される安定参照。filteredIds 変更時のみ再フィルタリングをトリガーする
  useEffect(() => {
    api.onFilterChanged()
  }, [filteredIds])

  // Re-fetch after save
  // eslint-disable-next-line react-hooks/exhaustive-deps -- hasRequiredSaveData の変化のみをトリガーとし、その時点の最新 model/fetchFilteredIds を読む
  useEffect(() => {
    const isAfterSavedOrRefreshed =
      prevHasRequiredSaveData && !hasRequiredSaveData
    if (isAfterSavedOrRefreshed && model) {
      fetchFilteredIds(model as DateFilter).then(() => {
        addedRowIds.current = []
      })
    }
  }, [hasRequiredSaveData])

  useGridFilter({
    doesFilterPass(params: IDoesFilterPassParams) {
      // Don't filter new rows
      if (params.data.added) {
        if (addedRowIds.current && params.node.id) {
          addedRowIds.current.push(params.node.id)
        }
        return true
      }
      if (
        addedRowIds.current &&
        params.node.id &&
        addedRowIds.current.includes(params.node.id)
      ) {
        return true
      }
      if (!filteredIds) return true
      return params.node.id ? filteredIds.has(params.node.id) : false
    },

    getModelAsString() {
      if (!model) return ''
      return getDateModelAsString(operator, dateValue)
    },
  })

  return (
    <BulkSheetFilter>
      <Box sx={{ display: 'flex' }}>
        <FilterSelect
          variant="outlined"
          value={operator}
          onChange={e =>
            onModelChange(
              buildDateFilterModel(
                e.target.value as DateFilterOperator,
                dateValue
              )
            )
          }
          sx={{ width: '100px' }}
        >
          {Object.values(DateFilterOperator).map(v => (
            <option key={`text-filter-option-${v}`} value={v}>
              {getLabel(v)}
            </option>
          ))}
        </FilterSelect>
        <Box sx={{ width: '110px', margin: '0 5px' }}>
          <DateInput
            value={dateValue}
            onChange={v => onModelChange(buildDateFilterModel(operator, v))}
          />
        </Box>
      </Box>
      <FilterFooter loading={loading} onClick={() => onModelChange(null)} />
    </BulkSheetFilter>
  )
}
